import { useCallback, useMemo, useState } from 'react'
import { consultarCancelacionDependientesOt } from '../api/common'

/**
 * useAccionParejaOt — hook reutilizable para envolver acciones sobre EquipmentInOt
 * (activar / restaurar / aplazar / pendiente / desaprobar) con consulta previa de
 * pareja (padre + hermanos) en la misma OT.
 *
 * Se usa en TODAS las vistas que renderizan TableEquiposCompact (OtSelectMain y
 * Otselect.js) para garantizar comportamiento consistente.
 *
 * Spec: openspec/changes/items-adicionales-equipo-dependiente/specs/equipos-dependientes-sincronizacion-cancelacion.
 *
 * @param {Object} params
 * @param {string} params.otId — id de la OT actual (necesario para el endpoint).
 * @param {Array} params.equiposOtIni — lista de equipos actual (para fallback local).
 * @returns {{ envolverConPareja: Function, modalAccionParejaProps: Object }}
 *   - envolverConPareja({ handler, accionLabel, excluirEstados }) → función async(eq).
 *   - modalAccionParejaProps: props listos para pasar a <ModalConfirmarAccionPareja/>.
 */
export default function useAccionParejaOt({ otId, equiposOtIni = [] }) {
    const [modalAccionPareja, setModalAccionPareja] = useState({
        show: false,
        accionLabel: '',
        parejas: [],
        onAplicarAmbos: () => {},
        onSoloEste: () => {},
        onNoHacerNada: () => {},
    })

    const envolverConPareja = useCallback(({ handler, accionLabel, excluirEstados }) => async (eq) => {
        console.log('[accionPareja] START', accionLabel, 'eq._id:', eq?._id, 'otId:', otId, 'excluirEstados:', excluirEstados)
        try {
            const respuesta = await consultarCancelacionDependientesOt(otId, [eq._id], excluirEstados)
            const parejas = respuesta?.data?.parejas || []
            console.log('[accionPareja] backend devolvió parejas:', parejas.length)
            if (parejas.length === 0) {
                console.log('[accionPareja] sin pareja → ejecutando handler directo')
                await handler(eq)
                return
            }
            console.log('[accionPareja] abriendo modal genérico')
            setModalAccionPareja({
                show: true,
                accionLabel,
                parejas,
                onAplicarAmbos: async (equiposParejaCompletos) => {
                    console.log('[accionPareja] Aplicar a ambos, equiposParejaCompletos:', equiposParejaCompletos)
                    setModalAccionPareja(s => ({ ...s, show: false }))
                    await handler(eq)
                    for (const parejaEq of (equiposParejaCompletos || [])) {
                        if (!parejaEq?._id) continue
                        // Fallback: si `parejaEq` viene minimal, buscarlo en la lista local para completar.
                        const parejaFinal = (parejaEq.estado || parejaEq.snapshot)
                            ? parejaEq
                            : (equiposOtIni.find(e => String(e._id) === String(parejaEq._id)) || parejaEq)
                        console.log('[accionPareja] aplicando handler a pareja:', String(parejaEq._id), 'estado:', parejaFinal?.estado)
                        try {
                            await handler(parejaFinal)
                        } catch (err) {
                            console.warn(`[accionPareja] Falló ${accionLabel} en pareja ${parejaEq._id}:`, err.message)
                        }
                    }
                },
                onSoloEste: async () => {
                    setModalAccionPareja(s => ({ ...s, show: false }))
                    await handler(eq)
                },
                onNoHacerNada: () => setModalAccionPareja(s => ({ ...s, show: false })),
            })
        } catch (err) {
            console.warn('[accionPareja] Falló consulta de pareja, ejecutando handler directo:', err.message)
            await handler(eq)
        }
    }, [otId, equiposOtIni])

    const modalAccionParejaProps = useMemo(() => ({
        show: modalAccionPareja.show,
        accionLabel: modalAccionPareja.accionLabel,
        parejas: modalAccionPareja.parejas,
        onAplicarAmbos: modalAccionPareja.onAplicarAmbos,
        onSoloEste: modalAccionPareja.onSoloEste,
        onNoHacerNada: modalAccionPareja.onNoHacerNada,
    }), [modalAccionPareja])

    return { envolverConPareja, modalAccionParejaProps }
}
